import shaka from 'shaka-player';
import {
  CollectionWrapper,
  ProgressResponse,
  GroupedBoundingPoly,
  DataFetchedForDetectionType,
  ClusterMapItem,
} from '@worker';
import { namespace } from './store';
import { SummaryTDO } from '@cbsa-modules/universal';
import {
  AudiowaveFrame,
  BlurLevel,
  ClusterItemGroup,
  DetailTDO,
  MediaComment,
  MediaUser,
  TranscriptionViewable,
  TranscriptionViewState,
  UDRsPolyAsset,
  UDRsPolyAssetGroupSeriesItem,
} from './models';
import { Patch } from 'immer';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { GetActionCreatorPayloadT } from '@utils';
import {
  ADD_NEW_COMMENT,
  REMOVE_COMMENT,
  SET_FILTER_TOGGLE_ALL,
  SET_SHOW_FILTER_TYPE,
  UPDATE_COMMENT,
} from '@common-modules/mediaDetails/actions';
import {
  CANCEL_JOB,
  PROCESS_ENGINE_REQUEST_OVERLAY,
} from '@common-modules/engines/optical-tracking';
import { ExternalIntegrationConfig } from '@helpers/externalIntegrationApi';
import type { StatusHistoryNoteOutcome } from '@common-modules/statusHistory/models';

export interface DetectionAssetStoreSlice {
  readonly [namespace]: DetectionAssetState;
}

export interface DetectionAssetState {
  readonly loaded: boolean;

  readonly facesSelectedEngineId: string | null;

  readonly detectionClusterGroups: { [groupId: string]: ClusterItemGroup };
  readonly udrClusterGroups: { [groupId: string]: ClusterItemGroup };

  readonly clusterMap: {
    readonly [clusterId: string]: ClusterMapItem;
  };

  readonly detectionCollections?: {
    [key: string]: CollectionWrapper | undefined;
  };
  readonly transcription?: TranscriptionViewable;
  readonly transcriptionView: TranscriptionViewState;
  readonly progress: ProgressResponse;

  readonly tdo: DetailTDO | null;
  readonly tdoIsChanged: boolean;
  /** VE-27765: bumped on every edit; see reducers/tdoChanged.ts. Never decreases. */
  readonly saveRev: number;
  readonly tdoHasPermissions: boolean | undefined;

  readonly currentPosition: number;
  /**
   * Set when the playhead was moved by a seek rather than by playback, and
   * consumed by the next `UPDATE_CURRENT_TIME_MEDIA_PLAYER`, which then collapses
   * `previousPosition` onto `currentPosition`.
   *
   * A seek differs from a playback step in KIND, not in size, which is why this
   * exists rather than a threshold on the delta: a 1.5s seek and a 1.5s step at
   * 8x playback are the same number (VE-25876).
   *
   * **Obligation for new seek paths.** Coverage here is a convention, not an
   * invariant the types can enforce: every way the playhead can move without
   * media playing through must reach the reducer as a flagged action — SEEK_MEDIA
   * for app-initiated jumps, or the player's own PLAYER_SEEKED as the catch-all,
   * which also covers a continuous scrub because it fires once per sample. Those
   * two are the whole list. A path that dispatches neither is not caught by
   * anything narrower than SAMPLE_WINDOW_BACKSTOP_MS, so it reports up to ~10s of
   * ground the playhead skipped as "on screen". Add a seek path, dispatch one of
   * these.
   *
   * Note that START_DRAG_AND_DROP_POSITION_BAR looks like a third option and is
   * not one: nothing in the app dispatches it, so `dragAndDropPositionBar` is
   * always false and the branch reading it in UPDATE_CURRENT_TIME_MEDIA_PLAYER is
   * dead. See plans/cleanup/VE-25876-dead-position-bar-drag-actions.md.
   */
  readonly playheadJumped: boolean;
  /**
   * The playhead position sampled immediately before `currentPosition`.
   *
   * `currentPosition` is fed by the DOM `timeupdate` event (~4Hz), not by the
   * video's frame callback, so consecutive values can be ~250ms apart. Anything
   * asking "what was on screen" from a point query at `currentPosition` can
   * therefore step clean over a segment shorter than that gap. Keeping the
   * previous sample lets such a consumer ask about the interval instead
   * (VE-25876) — see `selectPlayheadActiveGroupIds`.
   */
  readonly previousPosition: number;

  readonly detectionSeriesLengthWithDeleted: undefined | number;

  readonly voiceMaskMediaUrl?: string;

  readonly backgroundBlurEdits: BackgroundBlurSlice[];

  readonly isTdoExternallyModified: boolean | undefined;
  readonly hasInvalidTdoData: boolean | undefined;
}

/**
 * [startMs, endMs, AudioEditDetails | null | undefined]
 */
export type BackgroundBlurSlice = [
  number,
  number,
  { blurLevel: BlurLevel } | undefined | null,
];

export interface FaceDetectionInfo {
  isFetching: boolean;
  isProcessing: boolean;
  isReady: boolean;
  statusMessage: string;
}

interface BasicIntegration {
  authErrorMessage?: string;
  errorMessage?: string;
  successMessage?: string;

  isFormShown: boolean;
  isSendingFile: boolean;
  isStatusDialogShown: boolean;
}
export type GovQAIntegration = BasicIntegration & {
  sessionId?: string;
};

export type FOIAXpressIntegration = BasicIntegration;
export type ExternalIntegration = BasicIntegration & {
  config?: ExternalIntegrationConfig;
};
export type CasepointIntegration = BasicIntegration;
export type ExterroIntegration = BasicIntegration;
export type NuixIntegration = BasicIntegration;

export interface DeletedOverlaysCache {
  overlayIdsMap: { [key: string]: number };
  overlaySubsegmentIdsMap: { [key: string]: number };
}

export interface Extra {
  [param: string]: any;
  pendingDeletedRedactedMediaIds: string[];
  pendingDeletedAuditLogIds: string[];
  readonly user?: MediaUser;
  faceDetection: FaceDetectionInfo;
  redactionMediaDataReady: boolean;

  // TODO might want to move detection/udr parameter below to the DetectionAssetState?
  readonly udrCollection?: CollectionWrapper;
  readonly textOverlayCollection?: CollectionWrapper;
  readonly hasSavedUdrs: boolean | undefined; // used for early check whether there are udrs using redaction-media-data details
  readonly hasSavedDetections: boolean | undefined; // used for early check whether there are detection results using presence of vtn-standard asset
  readonly udrClusterGroupsLoaded: boolean;
  readonly detectionClusterGroupsLoaded: boolean;

  udrsState: {
    sprayPaintEdgeUdr?: UDRsPolyAssetGroupSeriesItem;
    udrBoundingPolyBeingUpdated?: GroupedBoundingPoly;
    selectedUDRGroupId?: string;
    udrAsset: UDRsPolyAsset;
    localUDRAsset: UDRsPolyAsset;
    regularModePlaybackSpeed: number;
  };

  readonly hasAudio: boolean;
  readonly videoLoaded: boolean;
  readonly dataFetchedForDetectionType: DataFetchedForDetectionType;

  integrations: {
    govQa: GovQAIntegration;
    foiaXpress: FOIAXpressIntegration;
    casepoint: CasepointIntegration;
    exterro: ExterroIntegration;
    nuix: NuixIntegration;
    externalIntegration: ExternalIntegration;
  };

  audiowaves: AudiowaveFrame[];

  deletedOverlaysCache: DeletedOverlaysCache;

  caseMedia: {
    caseId: string;
    caseName: string;
    media: SummaryTDO[];
  };

  isCaseMediaDrawerOpen: boolean;

  mediaComments: { [key: string]: MediaComment };
  mediaCommentDrafts: { [commentId: string]: string };
  editingMediaCommentId: string | null;
  mediaCommentsUsers: { [key: string]: MediaUser };
  isHideComment: boolean;
  isFetchingComments: boolean;
  selectedMediaComments: string[];
  lastSaveMediaDataDisplayTime?: string;
  isSaveRunning: boolean;
  isSaveFailed: boolean;
  isBannerDismissed: boolean;
  pendingLockedStatus: string | null;
  /** Correction note parked with pendingLockedStatus (VE-26730). */
  pendingLockedStatusNote: string | null;
  /** Reply parked with pendingLockedStatus (VE-26730), same reason as the note. */
  pendingLockedStatusResponse: string | null;
  pendingLockedStatusResponseType: StatusHistoryNoteOutcome | null;
  isBasicUserQuerySupported: boolean;
  isPlayerSeeked: boolean;

  tdoLockInfo: {
    isLocked: boolean;
    name?: string;
  };
  // Redaction status supplied by an editor that doesn't populate this slice's
  // DetailTDO `tags` (e.g. the document editor). When set, the role-permission
  // selector prefers it over the tag-derived status for the status-edit-lock
  // check. `null` for the video flow, which derives status from `tags`.
  externalRedactionStatus: string | null;
  thumbnailTracks?: shaka.Track | null;
  shakaPlayer?: shaka.Player | null;
  tdoLocks: { id: string }[];
}

export interface Undo {
  history: {
    past: MaxDepthArray<
      | {
          patches: Patch[];
          inversePatches: Patch[];
          metadata?: HistoryMetadata;
        }
      | {
          action:
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof UPDATE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof UPDATE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<
                    typeof PROCESS_ENGINE_REQUEST_OVERLAY
                  >
                >;
                payload: GetActionCreatorPayloadT<
                  typeof PROCESS_ENGINE_REQUEST_OVERLAY
                >;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>;
              };
          inverseAction:
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof UPDATE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof UPDATE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof REMOVE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof REMOVE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof CANCEL_JOB>
                >;
                payload: GetActionCreatorPayloadT<typeof CANCEL_JOB>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>;
              };
        }
      | null // TODO we probably do not need the null type anymore - keep for now until investigate further
      | {
          metadata?: HistoryMetadata;
        }
    >;
    future: Array<
      | {
          patches: Patch[];
          inversePatches: Patch[];
          metadata?: HistoryMetadata;
        }
      | {
          action:
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof UPDATE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof UPDATE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<
                    typeof PROCESS_ENGINE_REQUEST_OVERLAY
                  >
                >;
                payload: GetActionCreatorPayloadT<
                  typeof PROCESS_ENGINE_REQUEST_OVERLAY
                >;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>;
              };
          inverseAction:
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof UPDATE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof UPDATE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof ADD_NEW_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof REMOVE_COMMENT>
                >;
                payload: GetActionCreatorPayloadT<typeof REMOVE_COMMENT>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof CANCEL_JOB>
                >;
                payload: GetActionCreatorPayloadT<typeof CANCEL_JOB>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_FILTER_TOGGLE_ALL>;
              }
            | {
                creator: ActionCreatorWithPayload<
                  GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>
                >;
                payload: GetActionCreatorPayloadT<typeof SET_SHOW_FILTER_TYPE>;
              };
        }
      | null // TODO we probably do not need the null type anymore - keep for now until investigate further
      | {
          metadata?: HistoryMetadata;
        }
    >;
  };
}

export class MaxDepthArray<T> extends Array<T> {
  public static readonly MAX_DEPTH = 20;
  constructor(...elements: T[]) {
    super(...elements);
  }

  public push(value: T): number {
    while (this.length >= MaxDepthArray.MAX_DEPTH) {
      super.shift();
    }
    return super.push(value);
  }
}

export interface HistoryMetadata {
  description?: string;
}
